import React from 'react';

import {ImageTeaser} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/teaser.types';
import {useTrackOriginClick} from '@aboutyou/tracking';
import {
  Image,
  ImageRatio,
  InfoIconWithLink,
  mediaDesktopAndUp,
  mediaLargeScreens,
  mediaMinXSBreakpoint,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';

// images are technically never rendered in a higher resolution than ~375px on screen. Therefore a maximum of 700px on a retina image should be more than enough to make all screens happy
const IMAGE_WIDTHS = [200, 400, 600, 700];
const IMAGE_BREAKPOINTS = [
  {breakpoint: mediaDesktopAndUp, columns: 4},
  {breakpoint: mediaLargeScreens, columns: 8},
  {columns: 2},
];

interface ImageWrapperProps {
  imageTeaser: ImageTeaser;
  maxImageWidth?: number;
  dataTestId: string;
  teaserType?: 'drop' | 'imageTeaser';
}

export const ImageWrapper: React.FC<ImageWrapperProps> = ({
  imageTeaser,
  maxImageWidth,
  dataTestId,
  teaserType = 'imageTeaser',
}) => {
  const isLandscapeImage = Boolean(imageTeaser.landscapeImage.url);
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const trackOriginClick = useTrackOriginClick();

  const imageData = isLandscapeImage
    ? imageTeaser.landscapeImage
    : imageTeaser.portraitImage;
  const aspectRatio = isLandscapeImage ? '4:3' : '3:4';

  // image clusters (eg: inspiration, stories, sports...) have a fixed max-width, so we can simply use this (and its double for retina display) for the desired image widths
  const imageWidths = maxImageWidth
    ? [maxImageWidth, maxImageWidth * 2]
    : IMAGE_WIDTHS;

  return (
    <ImageLinkWrapper>
      <StyledImageWrapper
        $ratio={aspectRatio}
        $maxImageWidth={maxImageWidth}
        $teaserType={teaserType}
        data-testid={dataTestId}
        alt={imageData.description}
        importance="low"
        loading="lazy"
        primaryResizeSide="height"
        ratio={aspectRatio}
        src={imageData.url}
        breakpoints={IMAGE_BREAKPOINTS}
        widths={imageWidths}
        onClick={trackOriginClick('navigation_bar')}
      />
      {imageTeaser.infoIcon?.cta.linkTarget.url && (
        <InfoIconWithLink
          to={imageTeaser.infoIcon?.cta.linkTarget.url}
          renderAsAButton
          aria-label={getAriaLabelFromUrl(
            imageTeaser.infoIcon?.cta.linkTarget.url,
          )}
        />
      )}
    </ImageLinkWrapper>
  );
};

const StyledImageWrapper = styled(Image)<{
  $ratio?: ImageRatio;
  $maxImageWidth?: number;
  $teaserType?: 'drop' | 'imageTeaser';
}>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.3s ease;

  @media only screen and (min-width: ${mediaMinXSBreakpoint}px) {
    aspect-ratio: ${props =>
      props.$ratio ? props.$ratio.replace(':', '/') : ''};

    height: 100%;
  }

  @media ${mediaDesktopAndUp} {
    min-width: ${props =>
      props.$maxImageWidth || props.$teaserType === 'drop' ? `unset` : '275px'};
  }
  max-width: ${props =>
    props.$maxImageWidth ? `${props.$maxImageWidth}px` : '375px'};

  &:hover {
    transform: scale(1.03);
  }
`;

const ImageLinkWrapper = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;
